import { AttomDataType } from '@alpinehomeair/common';

export type PropertySnapshotTask = {
  key: string;
  label: string;
  value: string;
};

const INSULATION_BY_CONDITION: Record<string, string> = {
  POOR: 'Poor',
  AVERAGE: 'Good',
  GOOD: 'Great',
  EXCELLENT: 'Excellent'
};

const toPositiveNumber = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) && value > 0
    ? value
    : null;

const toTitleCase = (value: string): string =>
  value
    .toLowerCase()
    .split(/\s+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

const toDisplayString = (value: unknown): string | null =>
  typeof value === 'string' && value.trim() ? toTitleCase(value.trim()) : null;

const formatNumber = (value: number): string => value.toLocaleString('en-US');

const resolveYearBuilt = (attomData: AttomDataType): number | null => {
  const yearBuilt = toPositiveNumber(attomData.summary.yearBuilt);
  const improvementsYear = toPositiveNumber(
    Number(
      attomData.building.construction.propertyStructureMajorImprovementsYear
    )
  );

  if (yearBuilt && improvementsYear) {
    return Math.max(yearBuilt, improvementsYear);
  }

  return yearBuilt ?? improvementsYear;
};

const resolveInsulationLevel = (attomData: AttomDataType): string | null => {
  const { condition } = attomData.building.construction;

  if (typeof condition !== 'string') {
    return null;
  }

  return INSULATION_BY_CONDITION[condition.trim().toUpperCase()] ?? null;
};

export const buildPropertySnapshotTasks = (
  attomData: AttomDataType
): PropertySnapshotTask[] => {
  const tasks: PropertySnapshotTask[] = [];
  const squareFootage =
    toPositiveNumber(attomData.building.size.bldgSize) ??
    toPositiveNumber(attomData.building.size.livingSize);
  const yearBuilt = resolveYearBuilt(attomData);
  const levels = toPositiveNumber(attomData.building.summary.levels);
  const bedrooms = toPositiveNumber(attomData.building.rooms.beds);
  const bathrooms = toPositiveNumber(attomData.building.rooms.bathsTotal);
  const fireplaces = toPositiveNumber(attomData.building.interior.fplcCount);
  const hasBasement = Boolean(
    toPositiveNumber(attomData.building.interior.bsmtSize)
  );
  const insulationLevel = resolveInsulationLevel(attomData);
  const heatingType = toDisplayString(attomData.utilities.heatingType);
  const coolingType = toDisplayString(attomData.utilities.coolingType);

  if (squareFootage) {
    tasks.push({
      key: 'squareFootage',
      label: 'Square Footage',
      value: `${formatNumber(squareFootage)} sq ft`
    });
  }

  if (yearBuilt) {
    tasks.push({
      key: 'yearBuilt',
      label: 'Year Built / Renovated',
      value: String(yearBuilt)
    });
  }

  if (levels) {
    tasks.push({
      key: 'levels',
      label: 'Number of Floors',
      value: String(levels)
    });
  }

  if (bedrooms) {
    tasks.push({
      key: 'bedrooms',
      label: 'Bedrooms',
      value: String(bedrooms)
    });
  }

  if (bathrooms) {
    tasks.push({
      key: 'bathrooms',
      label: 'Bathrooms',
      value: String(bathrooms)
    });
  }

  if (fireplaces) {
    tasks.push({
      key: 'fireplaces',
      label: 'Fireplaces',
      value: String(fireplaces)
    });
  }

  if (hasBasement) {
    tasks.push({
      key: 'basement',
      label: 'Foundation Type',
      value: 'Basement'
    });
  }

  if (insulationLevel) {
    tasks.push({
      key: 'insulation',
      label: 'Insulation Level',
      value: insulationLevel
    });
  }

  if (heatingType) {
    tasks.push({
      key: 'heating',
      label: 'Heating',
      value: heatingType
    });
  }

  if (coolingType) {
    tasks.push({
      key: 'cooling',
      label: 'Cooling',
      value: coolingType
    });
  }

  return tasks;
};
